import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { trackFunnelEvent } from '../../utils/analytics';
import { InterpretationCard } from '../shared/InterpretationCard';
import { UpgradePrompt } from '../shared/UpgradePrompt';
import { Spinner } from '../ui/Spinner';

interface PlanetEntry { sign: string; degree: number }
interface NatalChartRow {
  planets: Record<string, PlanetEntry>;
  ascendant_sign: string | null;
}

const PT: Record<string, string> = {
  sun: 'Sol', moon: 'Lua', mercury: 'Mercúrio', venus: 'Vênus',
  mars: 'Marte', jupiter: 'Júpiter', saturn: 'Saturno',
  uranus: 'Urano', neptune: 'Netuno', pluto: 'Plutão',
};
const EMOJI: Record<string, string> = {
  sun: '☀️', moon: '🌙', mercury: '☿', venus: '💖',
  mars: '⚔️', jupiter: '♃', saturn: '♄',
  uranus: '♅', neptune: '♆', pluto: '♇',
};

function planetInterpretation(planet: string, sign: string): string {
  const tips: Record<string, string> = {
    sun:     `Seu Sol em ${sign} representa sua essência vital, propósito de vida e forma como você se expressa ao mundo. Descubra os talentos únicos que este posicionamento revela.`,
    moon:    `Sua Lua em ${sign} governa suas emoções, intuição e vida interior. Compreenda seus padrões emocionais e como você busca segurança afetiva.`,
    mercury: `Seu Mercúrio em ${sign} revela como você pensa, se comunica e aprende. Este posicionamento influencia sua inteligência e estilo de expressão verbal.`,
    venus:   `Seu Vênus em ${sign} rege seus relacionamentos, valores estéticos e forma de dar e receber amor. Descubra o que você atrai e valoriza.`,
    mars:    `Seu Marte em ${sign} governa sua energia, coragem e impulso para agir. Entenda sua força motriz e como canalizar sua ambição.`,
    jupiter: `Seu Júpiter em ${sign} aponta onde você encontra crescimento, abundância e boa sorte. Descubra seus caminhos de expansão e prosperidade.`,
    saturn:  `Seu Saturno em ${sign} revela suas lições kármicas, disciplina e conquistas a longo prazo. Entenda os desafios que moldam seu caráter.`,
    uranus:  `Seu Urano em ${sign} mostra onde você busca liberdade, inovação e ruptura com o passado. Descubra sua originalidade geracional.`,
    neptune: `Seu Netuno em ${sign} governa espiritualidade, sonhos e ilusões. Compreenda sua conexão com o transcendente e o invisível.`,
    pluto:   `Seu Plutão em ${sign} representa transformação profunda, poder e regeneração. Descubra onde você passa por renascimentos ao longo da vida.`,
  };
  return tips[planet] ?? `Descubra como ${PT[planet] ?? planet} em ${sign} influencia sua jornada.`;
}

export function FreeUserDashboard({ userId }: { userId: string }) {
  const [chart, setChart] = useState<NatalChartRow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackFunnelEvent('view_chart');
    supabase
      .from('natal_charts')
      .select('planets, ascendant_sign')
      .eq('user_id', userId)
      .order('calculated_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        setChart(data as NatalChartRow | null);
        setLoading(false);
      });
  }, [userId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <Spinner />
      </div>
    );
  }

  if (!chart?.planets) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
        Calcule seu mapa natal para ver suas leituras.
      </div>
    );
  }

  const FREE_PLANETS  = ['sun', 'moon'];
  const LOCKED_PLANETS = ['mercury', 'venus', 'mars', 'jupiter', 'saturn'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* Free readings */}
      <div>
        <p style={{ fontSize: 'var(--text-caption)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
          Suas leituras gratuitas
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {FREE_PLANETS.map(key => {
            const p = chart.planets[key];
            if (!p) return null;
            return (
              <InterpretationCard
                key={key}
                title={`${EMOJI[key] ?? ''} ${PT[key] ?? key}`}
                subtitle={`${PT[key] ?? key} em ${p.sign}`}
                content={planetInterpretation(key, p.sign)}
                unlocked
              />
            );
          })}
          {chart.ascendant_sign && (
            <InterpretationCard
              title="⬆️ Ascendente"
              subtitle={`Ascendente em ${chart.ascendant_sign}`}
              content={`Seu Ascendente em ${chart.ascendant_sign} é a máscara que você apresenta ao mundo, a primeira impressão que as pessoas têm de você e como você inicia novos ciclos de vida.`}
              unlocked
            />
          )}
        </div>
      </div>

      {/* Locked planets */}
      <div>
        <p style={{ fontSize: 'var(--text-caption)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
          Continue sua jornada, Premium
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {LOCKED_PLANETS.map(key => {
            const p = chart.planets[key];
            if (!p) return null;
            return (
              <InterpretationCard
                key={key}
                title={`${EMOJI[key] ?? ''} ${PT[key] ?? key}`}
                subtitle={`${PT[key] ?? key} em ${p.sign}`}
                content={planetInterpretation(key, p.sign)}
                unlocked={false}
                onUpgradeClick={() => trackFunnelEvent('paywall_hit', { planet: key })}
              />
            );
          })}
        </div>
      </div>

      {/* Upgrade CTA */}
      <UpgradePrompt
        title="Desbloqueie todos os planetas"
        benefits={[
          'Interpretação completa de todos os 10 planetas',
          'Previsões personalizadas diárias, semanais e mensais',
          'Sinastria real, compatibilidade entre mapas',
          'Acesso a 16+ sistemas esotéricos',
          'Sem limites, sem anúncios',
        ]}
        price="R$ 24,90/mês"
        ctaText="Assinar Agora"
        source="free_dashboard"
      />
    </div>
  );
}
